import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Select, Switch, Button, Tag, Divider, Tooltip, Empty } from 'antd';
import { toast } from 'react-toastify';
import { ChevronUp, ChevronDown } from 'lucide-react';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import {
  get_departments,
  bp_get_role,
  get_employee,
  get_approval_workflow_config,
  save_approval_workflow_config,
} from '@/utils/api';
import { isApprovalRole } from '@/utils/approvalRoles';

// ==============================|| DEMAND BATCH — WORKFLOW CONFIGURATION MODAL ||============================== //

const MODULE_NAME = 'Demand Batch';

const emptyStep = () => ({
  key: Date.now() + Math.random(),
  department_id: undefined,
  role_id: undefined,
  approver_ids: [],
  is_mandatory: true,
  can_reject: true,
  can_skip: false,
  esign_required: false,
});

const WorkflowConfigModal = ({ open, onClose }) => {
  const [workflowName, setWorkflowName] = useState('');
  const [steps, setSteps] = useState([emptyStep()]);
  const [departmentList, setDepartmentList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    get_departments((res) => setDepartmentList(res?.data?.departdata || []));
    bp_get_role((res) => setRoleList(res?.data?.data || []));
    get_employee((res) => setEmployeeList(res?.data?.data || []));

    setLoading(true);
    get_approval_workflow_config(MODULE_NAME, (data) => {
      setLoading(false);
      if (data?.success && data?.data) {
        setWorkflowName(data.data.workflow_name || '');
        setSteps(
          (data.data.steps || []).map((s) => {
            const fromIds = Array.isArray(s.approver_ids) ? s.approver_ids.filter(Boolean) : [];
            const fallback = s.approver_id ? [s.approver_id] : [];
            return {
              key: s.id ?? Date.now() + Math.random(),
              department_id: s.department_id,
              role_id: s.role_id,
              approver_ids: fromIds.length ? fromIds : fallback,
              is_mandatory: s.is_mandatory ?? true,
              can_reject: s.can_reject ?? true,
              can_skip: s.can_skip ?? false,
              esign_required: s.esign_required ?? false,
            };
          })
        );
        if (!data.data.steps?.length) setSteps([emptyStep()]);
      } else {
        setWorkflowName('');
        setSteps([emptyStep()]);
      }
    });
  }, [open]);

  const roleOptions = roleList.filter(isApprovalRole);

  const updateStep = (key, field, value) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));

  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (key) => setSteps((prev) => (prev.length > 1 ? prev.filter((s) => s.key !== key) : prev));

  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = () => {
    if (!workflowName.trim()) {
      toast.error('Workflow name is required');
      return;
    }
    if (
      steps.some(
        (s) => !s.department_id || !s.role_id || !Array.isArray(s.approver_ids) || s.approver_ids.length === 0
      )
    ) {
      toast.error('Every step needs a Department, Role, and at least one Approver');
      return;
    }

    setSaving(true);
    save_approval_workflow_config(
      {
        module: MODULE_NAME,
        workflow_name: workflowName.trim(),
        steps: steps.map((s, index) => ({
          sequence: index + 1,
          department_id: s.department_id,
          role_id: s.role_id,
          // Multi-select primary payload; approver_id kept as first for older readers.
          approver_ids: s.approver_ids,
          approver_id: s.approver_ids[0],
          is_mandatory: s.is_mandatory,
          can_reject: s.can_reject,
          can_skip: s.can_skip,
          esign_required: s.esign_required,
        })),
      },
      (data) => {
        setSaving(false);
        if (data?.success) {
          toast.success('Approval workflow saved');
          onClose();
        } else {
          toast.error(data?.message || 'Failed to save workflow');
        }
      },
      { onError: () => setSaving(false) }
    );
  };

  return (
    <PageModal
      icon={<Icons.Workflow size={22} />}
      title="Configure Demand Batch Approval Workflow"
      description="The ordered approval steps a production demand goes through."
      open={open}
      onCancel={onClose}
      width={1140}
      destroyOnHidden
    >
      {loading ? (
        <Empty description="Loading..." />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Input
              style={{ maxWidth: 360 }}
              placeholder="Workflow Name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
            />
            <Tag color="blue">Module: {MODULE_NAME}</Tag>
          </div>

          <Divider style={{ margin: '4px 0' }} />

          <div className="flex flex-col gap-3">
            {steps.map((step, index) => (
              <div
                key={step.key}
                className="flex items-center gap-2 flex-wrap"
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}
              >
                <Tag color="purple">Step {index + 1}</Tag>

                <Select
                  className="flex-1"
                  style={{ minWidth: 160 }}
                  placeholder="Department"
                  value={step.department_id}
                  onChange={(v) => updateStep(step.key, 'department_id', v)}
                  options={departmentList.map((d) => ({ value: d.id, label: d.department_name }))}
                />

                <Select
                  className="flex-1"
                  style={{ minWidth: 140 }}
                  placeholder="Role"
                  value={step.role_id}
                  onChange={(v) => updateStep(step.key, 'role_id', v)}
                  options={roleOptions.map((r) => ({ value: r.role_id, label: r.role }))}
                />

                <Select
                  mode="multiple"
                  className="flex-1"
                  style={{ minWidth: 220 }}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Approvers (multi-select)"
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length}`}
                  value={step.approver_ids}
                  onChange={(v) => updateStep(step.key, 'approver_ids', v)}
                  options={employeeList.map((e) => ({
                    value: e.employee_id,
                    label: e.employee_name || e.username,
                  }))}
                />

                <Tooltip title="Mandatory">
                  <div className="flex items-center gap-1">
                    <span className="text-xs">Mandatory</span>
                    <Switch
                      size="small"
                      checked={step.is_mandatory}
                      onChange={(v) => updateStep(step.key, 'is_mandatory', v)}
                    />
                  </div>
                </Tooltip>

                <Tooltip title="Can Reject">
                  <div className="flex items-center gap-1">
                    <span className="text-xs">Can Reject</span>
                    <Switch
                      size="small"
                      checked={step.can_reject}
                      onChange={(v) => updateStep(step.key, 'can_reject', v)}
                    />
                  </div>
                </Tooltip>

                <Tooltip title="E-Signature Required">
                  <div className="flex items-center gap-1">
                    <span className="text-xs">E-Sign</span>
                    <Switch
                      size="small"
                      checked={step.esign_required}
                      onChange={(v) => updateStep(step.key, 'esign_required', v)}
                    />
                  </div>
                </Tooltip>

                <div className="flex items-center gap-1">
                  <ChevronUp
                    size={16}
                    className={`cursor-pointer ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:text-black'}`}
                    onClick={() => index > 0 && moveStep(index, -1)}
                  />
                  <ChevronDown
                    size={16}
                    className={`cursor-pointer ${index === steps.length - 1 ? 'text-gray-300' : 'text-gray-600 hover:text-black'}`}
                    onClick={() => index < steps.length - 1 && moveStep(index, 1)}
                  />
                  <Icons.Trash2
                    className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800"
                    onClick={() => removeStep(step.key)}
                  />
                </div>
              </div>
            ))}
          </div>

          <Button type="dashed" icon={<Icons.Plus className="w-4 h-4" />} onClick={addStep}>
            Add another step
          </Button>

          <div className="flex justify-end">
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save Workflow
            </Button>
          </div>
        </div>
      )}
    </PageModal>
  );
};

WorkflowConfigModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default WorkflowConfigModal;
